import { useCallback, useEffect, useRef, useState } from "react";
import {
  maxUploadBytesForPurpose,
  UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE,
} from "@nexus/contracts";
import { runScriptReviewUploadFlow } from "@/features/script-review/api/script-review.api";
import { Sentry } from "@/lib/sentry";

export type PersonaScriptReferenceUploadState =
  | "idle"
  | "uploading"
  | "confirming"
  | "ready"
  | "failed";

const REFERENCE_MAX_BYTES = maxUploadBytesForPurpose(UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE);
const MP4_CONTENT_TYPE = "video/mp4";

type TranslateFn = (key: string, defaultValue: string) => string;

export function usePersonaScriptReferenceUpload(opts: {
  tenantId: string | null;
  t?: TranslateFn;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [state, setState] = useState<PersonaScriptReferenceUploadState>("idle");
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    runIdRef.current += 1;
    setFile(null);
    setUploadId(null);
    setProgress(0);
    setState("idle");
    setError(null);
  }, []);

  const start = useCallback(
    (next: File) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const runId = ++runIdRef.current;
      const isStale = () => runIdRef.current !== runId;

      setFile(next);
      setUploadId(null);
      setProgress(0);
      setError(null);

      if (next.type !== MP4_CONTENT_TYPE) {
        setState("failed");
        setError(
          opts.t?.(
            "personaOpportunities.generateScripts.reference.invalidType",
            "Upload an mp4 file.",
          ) ?? "Upload an mp4 file.",
        );
        return;
      }
      if (next.size > REFERENCE_MAX_BYTES) {
        setState("failed");
        setError(
          opts.t?.(
            "personaOpportunities.generateScripts.reference.tooLarge",
            "Video reference must be 250MB or smaller.",
          ) ?? "Video reference must be 250MB or smaller.",
        );
        return;
      }
      if (!opts.tenantId) {
        setState("failed");
        setError(
          opts.t?.(
            "personaOpportunities.generateScripts.reference.noActiveOrg",
            "No active organization",
          ) ?? "No active organization",
        );
        return;
      }

      setState("uploading");

      void (async () => {
        try {
          const id = await runScriptReviewUploadFlow({
            tenantId: opts.tenantId!,
            file: next,
            purpose: UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE,
            contentType: MP4_CONTENT_TYPE,
            onProgress: (p) => {
              if (isStale()) return;
              setProgress(p);
              if (p >= 100) setState("confirming");
            },
            signal: controller.signal,
          });
          if (isStale()) return;
          setUploadId(id);
          setProgress(100);
          setState("ready");
        } catch (e) {
          if (isStale() || controller.signal.aborted) return;
          setState("failed");
          if (!(e instanceof Error && e.name === "AbortError")) {
            Sentry.captureException(e);
          }
          setError(
            opts.t?.(
              "personaOpportunities.generateScripts.reference.uploadFailed",
              "Upload failed — pick the file again.",
            ) ?? "Upload failed — pick the file again.",
          );
        }
      })();
    },
    [opts.tenantId, opts.t],
  );

  useEffect(() => () => abortRef.current?.abort(), []);

  return { file, uploadId, progress, state, error, start, reset };
}
